import {useCallback, useMemo, useRef} from 'react'
import {AtUri} from '@atproto/syntax'
import {
  type InfiniteData,
  type QueryClient,
  type QueryKey,
  useInfiniteQuery,
} from '@tanstack/react-query'

import {moderatePost} from '#/lib/moderation'
import {
  PROVIDER_COMPOSITION_QUERY_META,
  type ProviderCompositionResult,
} from '#/lib/provider-composition'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {
  composeAppViewProviderRead,
  requireComposedProviderValue,
} from '#/state/queries/provider-composition'
import {stripNonLocalBlockVisibility} from '#/state/queries/public-visibility'
import {useAppviewProviderClientFactory} from '#/state/session'
import {type SearchFilters} from '#/screens/Search/searchParams'
import {app} from '#/lexicons'
import {
  appendFromMe,
  buildSearchPostsV2Filters,
  extractSearchPostsParams,
} from './search-posts-params'
import {
  didOrHandleUriMatches,
  embedViewRecordToPostView,
  getEmbeddedPost,
} from './util'

const searchPostsQueryKeyRoot = 'search-posts'
const searchPostsV2QueryKey = ({
  query,
  sort,
  filters,
}: {
  query: string
  sort?: string
  filters?: SearchFilters
}) => [searchPostsQueryKeyRoot, query, sort, filters]

export type SearchPostsV2Page = app.bsky.feed.searchPostsV2.$OutputBody & {
  providerComposition?: ProviderCompositionResult<app.bsky.feed.searchPostsV2.$OutputBody>
}

export function useSearchPostsV2Query({
  query,
  sort,
  enabled,
  filters,
}: {
  query: string
  sort?: 'top' | 'latest'
  enabled?: boolean
  filters?: SearchFilters
}) {
  const providerClientFactory = useAppviewProviderClientFactory()
  const moderationOpts = useModerationOpts()
  const selectArgs = useMemo(
    () => ({
      isSearchingSpecificUser:
        /from:(\w+)/.test(query) || !!filters?.author || filters?.from === 'me',
      moderationOpts,
    }),
    [query, filters?.author, filters?.from, moderationOpts],
  )
  const lastRun = useRef<{
    data: InfiniteData<SearchPostsV2Page>
    args: typeof selectArgs
    result: InfiniteData<SearchPostsV2Page>
  } | null>(null)

  return useInfiniteQuery<
    SearchPostsV2Page,
    Error,
    InfiniteData<SearchPostsV2Page>,
    QueryKey,
    string | undefined
  >({
    meta: PROVIDER_COMPOSITION_QUERY_META,
    queryKey: searchPostsV2QueryKey({query, sort, filters}),
    queryFn: async ({pageParam, signal}) => {
      /*
       * Operators embedded in the query string (e.g. for back-compat links) are
       * merged with the explicit structured filters from the advanced search
       * dialog; see buildSearchPostsV2Filters for how the two sources combine.
       */
      const {q, ...embedded} = extractSearchPostsParams(query)
      /*
       * The generated params brand format-constrained strings (uri,
       * at-identifier, language) as template-literal types. The builder emits
       * plain strings parsed from user input, which are runtime-identical, so
       * the brand is asserted rather than re-validated here.
       */
      const builtFilters = buildSearchPostsV2Filters(
        embedded,
        filters,
      ) as app.bsky.feed.searchPostsV2.$Params
      const finalQuery = appendFromMe(q, filters?.from === 'me')
      const composed = await composeAppViewProviderRead(
        'search',
        (providerClient, _provider, context) =>
          providerClient.call(
            app.bsky.feed.searchPostsV2,
            {
              ...builtFilters,
              query: finalQuery,
              limit: 25,
              cursor: pageParam,
              /*
               * v2 calls the recency sort 'recent'; the rest of the app still
               * uses the v1 'latest' label.
               */
              sort: sort === 'latest' ? 'recent' : sort,
              allTime: true,
            },
            {signal: context.signal},
          ),
        {
          access: 'account-scoped',
          clientForProvider: providerClientFactory,
          signal,
        },
      )
      return {
        ...requireComposedProviderValue(composed),
        providerComposition: composed,
      }
    },
    initialPageParam: undefined,
    getNextPageParam: lastPage => lastPage.cursor,
    enabled: enabled ?? !!moderationOpts,
    select: useCallback(
      (data: InfiniteData<SearchPostsV2Page>) => {
        const {moderationOpts, isSearchingSpecificUser} = selectArgs

        /*
         * If a user applies the `from:<user>` filter, don't apply any
         * moderation. Note that if we add any more filtering logic below, we
         * may need to adjust this.
         */
        if (isSearchingSpecificUser) {
          return data
        }

        /*
         * Keep track of the last run and whether we can reuse some already
         * selected pages from there.
         */
        let reusedPages = []
        if (lastRun.current) {
          const {
            data: lastData,
            args: lastArgs,
            result: lastResult,
          } = lastRun.current
          let canReuse = true
          for (let key in selectArgs) {
            if (selectArgs.hasOwnProperty(key)) {
              if (
                (selectArgs as Record<string, unknown>)[key] !==
                (lastArgs as Record<string, unknown>)[key]
              ) {
                // Can't do reuse anything if any input has changed.
                canReuse = false
                break
              }
            }
          }
          if (canReuse) {
            for (let i = 0; i < data.pages.length; i++) {
              if (data.pages[i] && lastData.pages[i] === data.pages[i]) {
                reusedPages.push(lastResult.pages[i])
                continue
              }
              // Stop as soon as pages stop matching up.
              break
            }
          }
        }

        const result = {
          ...data,
          pages: [
            ...reusedPages,
            ...data.pages.slice(reusedPages.length).map(page => {
              return {
                ...page,
                posts: page.posts.filter(post => {
                  const mod = moderatePost(
                    stripNonLocalBlockVisibility(post),
                    moderationOpts!,
                  )
                  return !mod.ui('contentList').filter
                }),
              }
            }),
          ],
        }

        lastRun.current = {data, result, args: selectArgs}

        return result
      },
      [selectArgs],
    ),
  })
}

export function* findAllPostsInQueryData(
  queryClient: QueryClient,
  uri: string,
): Generator<app.bsky.feed.defs.PostView, undefined> {
  const queryDatas = queryClient.getQueriesData<
    InfiniteData<SearchPostsV2Page>
  >({
    queryKey: [searchPostsQueryKeyRoot],
  })
  const atUri = new AtUri(uri)

  for (const [_queryKey, queryData] of queryDatas) {
    if (!queryData?.pages) {
      continue
    }
    for (const page of queryData?.pages) {
      for (const post of page.posts) {
        if (didOrHandleUriMatches(atUri, post)) {
          yield post
        }

        const quotedPost = getEmbeddedPost(post.embed)
        if (quotedPost && didOrHandleUriMatches(atUri, quotedPost)) {
          yield embedViewRecordToPostView(quotedPost)
        }
      }
    }
  }
}
